import { useState } from "react";

import { courseToSteer } from "@nav-games/chartwork";
import { formatHeading } from "@nav-games/compass";

/// Course-to-Steer calculator for the Reference tab. Enter the track to
/// make good, your boat speed, and the tidal stream (set + rate); it
/// returns the heading to steer through the water and the resulting
/// speed over the ground. All directions are °True — convert the CTS to
/// a compass course with the Compass-error tools.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function NumField({
  label,
  value,
  onChange,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 120 }}>
      <span className="small muted">{label}</span>
      <input
        type="number"
        min={0}
        max={max}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
    </label>
  );
}

export function CtsCalculator() {
  const [groundTrack, setGroundTrack] = useState("90");
  const [boatSpeed, setBoatSpeed] = useState("5");
  const [set, setSet] = useState("180");
  const [rate, setRate] = useState("2");

  const nums = [groundTrack, boatSpeed, set, rate].map(Number);
  const valid =
    [groundTrack, boatSpeed, set, rate].every((s) => s.trim() !== "") &&
    nums.every(Number.isFinite) &&
    Number(boatSpeed) > 0;

  const result = valid
    ? courseToSteer({
        groundTrackDeg: Number(groundTrack),
        boatSpeedKts: Number(boatSpeed),
        setDeg: Number(set),
        rateKts: Number(rate),
      })
    : null;

  const offsetSide = result
    ? result.offsetDeg < -0.05
      ? "to port"
      : result.offsetDeg > 0.05
        ? "to starboard"
        : "on the track"
    : "";

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Course to steer calculator</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        All directions °True. Tidal <em>set</em> is the way the stream flows
        toward.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <NumField label="Ground track (°T)" value={groundTrack} onChange={setGroundTrack} max={359} />
        <NumField label="Boat speed (kt)" value={boatSpeed} onChange={setBoatSpeed} max={99} />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <NumField label="Tide set (°T)" value={set} onChange={setSet} max={359} />
        <NumField label="Tide rate (kt)" value={rate} onChange={setRate} max={20} />
      </div>

      {result && result.feasible ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "10px 4px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--accent)",
                background: "var(--accent-soft)",
              }}
            >
              <div className="small muted">Course to steer</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {formatHeading(result.courseToSteerDeg)}°T
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "10px 4px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border)",
              }}
            >
              <div className="small muted">Speed over ground</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {result.speedOverGroundKts.toFixed(1)} kt
              </div>
            </div>
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            Steer <strong>{Math.abs(Math.round(result.offsetDeg))}° {offsetSide}</strong>{" "}
            of the track to hold it against{" "}
            {Math.abs(result.tideAcrossKts).toFixed(1)} kt of cross-tide. Then
            convert {formatHeading(result.courseToSteerDeg)}°T to a compass
            course on the <strong>Compass error</strong> tab.
          </p>
        </>
      ) : result && !result.feasible ? (
        <p className="small" style={{ margin: 0, color: "var(--danger)" }}>
          The tide is too strong across the track for this boat speed — you
          can't hold this ground track. Slow the track demand or accept being
          set down-tide.
        </p>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter the track, boat speed and tide to compute the course to steer.
        </p>
      )}
    </div>
  );
}
